import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Lang, translations } from "../i18n";
import { writeSharedConsentCookies } from "../lib/consentCookies";

type Props = {
  lang: Lang;
};

const CookieConsent = ({ lang }: Props) => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const t = translations[lang].cookieConsent;

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    const consentDate = localStorage.getItem("cookie-consent-date");

    // Check if consent exists and is not older than 12 months (GDPR requirement)
    if (!consent) {
      setShow(true);
    } else if (consentDate) {
      const consentAge = Date.now() - parseInt(consentDate);
      const twelveMonths = 12 * 30 * 24 * 60 * 60 * 1000; // Approximate

      if (consentAge > twelveMonths) {
        // Consent expired, ask again
        localStorage.removeItem("cookie-consent");
        localStorage.removeItem("cookie-consent-date");
        localStorage.removeItem("cookie-preferences");
        setShow(true);
      }
    }
  }, []);

  const saveConsent = (preferences: any) => {
    const now = Date.now().toString();
    localStorage.setItem("cookie-consent", "accepted");
    localStorage.setItem("cookie-consent-date", now);
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));

    // Hand the decision to the app subdomain immediately (cross-domain cookies).
    writeSharedConsentCookies(preferences);

    // Notify listeners (e.g. MetaPixel / hooks) immediately in this tab
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("cookie-consent-changed", { detail: preferences })
      );
    }

    // Enable/disable cookies based on preferences
    if (preferences.analytics) {
      enableAnalytics();
    }
    if (preferences.marketing) {
      enableMarketing();
    }

    setShow(false);

    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const enableAnalytics = () => {
    // Load Google Analytics or other analytics scripts
      console.log("Analytics enabled");
  };

  const enableMarketing = () => {
    // Load marketing pixels/scripts
      console.log("Marketing enabled");
  };

  if (!show) return null;

  return (
    <>
      {/* Main Banner - styled to match app (dark, orange accent) */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 pb-4">
          <div className="bg-slate-900/95 border border-slate-800 shadow-xl rounded-2xl px-4 py-3 md:px-6 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex-1 pr-0 md:pr-4">
              <p className="text-xs md:text-sm text-slate-200">
                {t.bannerText}{" "}
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-orange-400 hover:text-orange-300 underline underline-offset-2 ml-1"
                >
                  {t.settingsLabel}
                </button>
                <span className="mx-1">·</span>
                <a
                  href="/cookie-policy"
                  className="text-slate-300 hover:text-white underline underline-offset-2"
                >
                  {t.learnMoreLabel}
                </a>
              </p>
            </div>
            <div className="flex items-center gap-2 self-end md:self-auto">
              <button
                onClick={acceptNecessary}
                className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm text-slate-200 border border-slate-700 rounded-lg hover:bg-slate-800 transition-colors"
              >
                {t.acceptNecessaryLabel}
              </button>
              <button
                onClick={acceptAll}
                className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 shadow-md shadow-orange-500/30 transition-colors"
              >
                {t.acceptAllLabel}
              </button>
              <button
                onClick={() => setShow(false)}
                className="ml-1 p-1 text-slate-500 hover:text-slate-300"
                aria-label="Close cookie banner"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Settings Modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {t.modalTitle}
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-slate-500 hover:text-slate-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/60">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">
                        {t.necessaryTitle}
                      </h4>
                      <p className="text-sm text-slate-400 mt-1">
                        {t.necessaryDescription}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/40">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">
                        {t.analyticsTitle}
                      </h4>
                      <p className="text-sm text-slate-400 mt-1">
                        {t.analyticsDescription}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      id="analytics"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="border border-slate-800 rounded-xl p-4 bg-slate-900/40">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-white">
                        {t.marketingTitle}
                      </h4>
                      <p className="text-sm text-slate-400 mt-1">
                        {t.marketingDescription}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked={false}
                      id="marketing"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 px-4 py-2 border border-slate-700 rounded-lg text-slate-200 hover:bg-slate-800 transition-colors"
                >
                  {t.cancelLabel}
                </button>
                <button
                  onClick={() => {
                    const analytics =
                      (
                        document.getElementById(
                          "analytics"
                        ) as HTMLInputElement | null
                      )?.checked || false;
                    const marketing =
                      (
                        document.getElementById(
                          "marketing"
                        ) as HTMLInputElement | null
                      )?.checked || false;
                    saveConsent({ necessary: true, analytics, marketing });
                    setShowDetails(false);
                  }}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 shadow-md shadow-orange-500/30 transition-colors"
                >
                  {t.saveLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
